import React from 'react';
import PropTypes from 'prop-types';
import { I18n } from 'react-redux-i18n-lite';
import { HowToList } from '@netexknowledge/smart-components';
import CustomIcon from '../../common/CustomIcon';
import { translateWithTokens } from '../../../services/translate/translateWithTokens';
import { DISCUSSION_HELP_COLORS } from './discussionTypeMeta';

const { accent, neutral } = DISCUSSION_HELP_COLORS;

/**
 * Nodes that replace the %{…} placeholders of the help texts: the list buttons and the tab names.
 */
export const getHelpTokens = () => ({
  plusIcon: <CustomIcon name="plus-button" size={11} color={accent.icon} />,
  moreIcon: <CustomIcon name="more-vert" size={12} color={accent.icon} />,
  scheduled: <strong>{I18n.t('discussions.tabs.future')}</strong>,
  finished: <strong>{I18n.t('discussions.tabs.finished')}</strong>,
  inProgress: <strong>{I18n.t('discussions.tabs.current')}</strong>,
  reply: <strong>{I18n.t('discussions.common.reply')}</strong>,
  openContent: <strong>{I18n.t('discussions.common.openContent')}</strong>,
  closeOption: <em>{I18n.t('discussions.common.options.closeDiscussion')}</em>
});

// The empty state reuses the popover steps, but some of them explain more there.
const STEPS = {
  staff: [
    { key: 'create', icon: 'plus-button', colors: accent, emptyDescription: 'discussions.empty.staff.steps.create' },
    { key: 'manage', icon: 'more-vert', colors: neutral, emptyDescription: 'discussions.empty.staff.steps.manage' },
    { key: 'reply', icon: 'chat-speech-balloon', colors: neutral, popoverOnly: true },
    { key: 'content', icon: 'workspace', colors: neutral }
  ],
  student: [
    {
      key: 'participate',
      icon: 'chat-speech-balloon',
      colors: accent,
      emptyDescription: 'discussions.empty.student.steps.participate'
    },
    {
      key: 'closing',
      icon: 'finish-flag',
      colors: neutral,
      emptyDescription: 'discussions.empty.student.steps.closing'
    },
    { key: 'openContent', icon: 'workspace', colors: neutral }
  ]
};

/**
 * `isStaff` picks the teacher steps (create and manage) or the student ones (take part).
 * `variant` "empty" drops the reply step for teachers and uses the longer descriptions.
 */
export const getDiscussionsHelpItems = ({ isStaff, variant = 'popover' }) => {
  const isEmpty = variant === 'empty';
  const tokens = getHelpTokens();

  return STEPS[isStaff ? 'staff' : 'student']
    .filter(step => !(isEmpty && step.popoverOnly))
    .map(step => ({
      key: step.key,
      icon: <CustomIcon name={step.icon} size={isEmpty ? 15 : 14} color={step.colors.icon} />,
      iconBackground: step.colors.background,
      title: I18n.t(`discussions.help.${step.key}.title`),
      description: translateWithTokens(
        isEmpty && step.emptyDescription ? step.emptyDescription : `discussions.help.${step.key}.description`,
        tokens
      )
    }));
};

/**
 * How discussions work for each role. Shared by the empty state (framed) and the help popover (compact).
 */
export default function DiscussionsHelp({ isStaff, variant, compact, framed }) {
  return <HowToList items={getDiscussionsHelpItems({ isStaff, variant })} compact={compact} framed={framed} />;
}

DiscussionsHelp.propTypes = {
  isStaff: PropTypes.bool,
  variant: PropTypes.oneOf(['popover', 'empty']),
  compact: PropTypes.bool,
  framed: PropTypes.bool
};
